/**
 * InsightsPanel - AI-generated insights and recommendations
 * Layout aligned with dashboard-static / prototype (US 2473)
 */
import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Drawer,
  IconButton
} from '@mui/material';
import {
  Refresh,
  Chat as ChatIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import insightsService from '../../services/insightsService';
import ChatAssistant from './ChatAssistant';

const InsightsPanel = ({ onChatWithInsight, maxItems = 4 }) => {
  const { t } = useTranslation();
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chatOpen, setChatOpen] = useState(false);
  const [selectedInsight, setSelectedInsight] = useState(null);

  useEffect(() => {
    if (chatOpen) {
      document.body.classList.add('insight-chat-open');
    } else {
      document.body.classList.remove('insight-chat-open');
    }

    return () => {
      document.body.classList.remove('insight-chat-open');
    };
  }, [chatOpen]);

  const getMockInsights = useCallback(() => {
    const actions = t('insights.mock.scheduleReview.actions', { returnObjects: true });

    return Array.from({ length: 4 }, (_, index) => ({
      insight_id: `mock-${index + 1}`,
      severity: 'warning',
      title: t('insights.mock.scheduleReview.title'),
      description: t('insights.mock.scheduleReview.description'),
      recommended_actions: Array.isArray(actions) ? actions : []
    }));
  }, [t]);

  const loadInsights = useCallback(async () => {
    setLoading(true);
    try {
      const data = await insightsService.getInsights({
        status: 'active',
        limit: maxItems
      });

      if (!data.insights || data.insights.length === 0) {
        console.log('No insights found, generating new insights...');
        try {
          const generateResult = await insightsService.generateInsights(30);
          if (generateResult.insights && generateResult.insights.length > 0) {
            setInsights(generateResult.insights);
          } else {
            setInsights([]);
          }
        } catch (genErr) {
          console.error('Failed to generate insights:', genErr);
          setInsights([]);
        }
      } else {
        setInsights(data.insights);
      }
    } catch (err) {
      console.error('Failed to load insights:', err);
      setInsights(getMockInsights());
    } finally {
      setLoading(false);
    }
  }, [getMockInsights, maxItems]);

  useEffect(() => {
    loadInsights();
  }, [loadInsights]);

  const handleDismiss = async (insightId, event) => {
    event.stopPropagation();
    try {
      await insightsService.acknowledgeInsight(insightId);
      setInsights((prev) => prev.filter((insight) => insight.insight_id !== insightId));
    } catch (err) {
      console.error('Failed to dismiss insight:', err);
    }
  };

  const handleChat = (insight, event) => {
    event.stopPropagation();
    if (onChatWithInsight) {
      onChatWithInsight(insight);
    } else {
      setSelectedInsight(insight);
      setChatOpen(true);
    }
  };

  const handleChatClose = () => {
    setChatOpen(false);
    setSelectedInsight(null);
  };

  if (loading) {
    return (
      <div className="insights">
        <div className="insights-header">
          <div className="insights-title">
            <h2>{t('insights.title')}</h2>
          </div>
        </div>
        <div className="insights-loading">
          <CircularProgress size={32} />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="insights">
        <div className="insights-header">
          <div className="insights-title">
            <h2>{t('insights.title')}</h2>
            {insights.length > 0 && (
              <span className="badge">{insights.length}</span>
            )}
          </div>
          <button
            type="button"
            className="icon-btn"
            onClick={loadInsights}
            aria-label={t('insights.refresh')}
          >
            <Refresh fontSize="small" />
          </button>
        </div>

        {insights.length > 0 ? (
          <div className="insights-grid">
            {insights.map((insight) => (
              <InsightCard
                key={insight.insight_id}
                insight={insight}
                onChat={(event) => handleChat(insight, event)}
                onDismiss={(event) => handleDismiss(insight.insight_id, event)}
              />
            ))}
          </div>
        ) : (
          <div className="insights-empty is-visible">
            <Typography component="p" sx={{ fontWeight: 600, mb: 1 }}>
              {t('insights.noInsights')}
            </Typography>
            <Typography component="p" sx={{ fontSize: '0.875rem' }}>
              {t('insights.noInsightsDescription')}
            </Typography>
          </div>
        )}
      </div>

      {chatOpen && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 0,
            right: 0,
            width: 100,
            height: 100,
            zIndex: 10000,
            pointerEvents: 'none'
          }}
        />
      )}

      <Drawer
        anchor="right"
        open={chatOpen}
        onClose={handleChatClose}
        sx={{ zIndex: 10001 }}
        ModalProps={{
          sx: { zIndex: 10001 },
          keepMounted: false
        }}
        slotProps={{
          backdrop: {
            sx: {
              zIndex: 10000,
              backgroundColor: 'rgba(0, 0, 0, 0.5)'
            }
          }
        }}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 420 },
            maxWidth: '100%',
            zIndex: 10002
          }
        }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box
            sx={{
              p: 2,
              borderBottom: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              background: 'linear-gradient(180deg, #38BCFF 0%, #016FF4 100%)',
              color: 'white'
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {t('insights.chatTitle')}
            </Typography>
            <IconButton onClick={handleChatClose} sx={{ color: 'white' }}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={{ flex: 1, overflow: 'hidden' }}>
            {selectedInsight && (
              <ChatAssistant
                floating={false}
                contextType="insight"
                height="100%"
                initialContext={selectedInsight}
                onContextCleared={() => {}}
              />
            )}
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

const InsightCard = ({ insight, onChat, onDismiss }) => {
  const { t } = useTranslation();
  const actions = insight.recommended_actions?.slice(0, 4) || [];

  return (
    <div className="insight-card">
      <div className="insight-header">
        <div className="insight-title">{insight.title}</div>
        <span className="insight-tag">{t('insights.severity.warning')}</span>
      </div>
      <div className="insight-desc">{insight.description}</div>
      {actions.length > 0 && (
        <ul className="insight-list">
          {actions.map((action, index) => (
            <li key={index}>{action}</li>
          ))}
        </ul>
      )}
      <div className="insight-actions">
        <button type="button" className="btn primary small" onClick={onChat}>
          <ChatIcon sx={{ width: 16, height: 16 }} />
          {t('insights.chat')}
        </button>
        <button type="button" className="btn danger small" onClick={onDismiss}>
          <CloseIcon sx={{ width: 16, height: 16 }} />
          {t('insights.dismiss')}
        </button>
      </div>
    </div>
  );
};

export default InsightsPanel;
